import { checkAuth } from 'providers/auth';
import { stringify } from 'qs';
import { UpdateManyResult } from 'ra-core';
import { addRefreshAuthToDataProvider, DataProvider, HttpError, OnError, OnSuccess } from 'react-admin';
import { AccountPayload } from 'types/Account';
import { Command, CommandExecution, CommandParameter, ExecutionStatus } from 'types/commands';
import { createClient, getIds, getQueryFromParams, setInterceptors } from './helpers';
import { defaultSettings } from './settings';

type DataProviderFactory<ResourceType extends string = string> = (
  apiUrl: string,
  customSettings?: Record<any, unknown>,
  tokenName?: string,
) => DataProvider<ResourceType>;

// Helper to parse form data
const parseIntoFormData = (data: any, method?: string) => {
  if (data.icon?.rawFile instanceof File) {
    let formData = new FormData();

    for (let property in data) {
      if (property === 'icon') {
        formData.append('icon', data.icon.rawFile, data.icon.title);
      } else if (typeof data[property] === 'object') {
        for (let property_id in data[property]) {
          formData.append(property + '[]', data[property][property_id]);
        }
      } else {
        formData.append(property, data[property]);
      }
    }

    if (method) {
      formData.append('_method', method);
      method = 'post';
    }

    let contentType = 'multipart/form-data';

    data = formData;
    return [data, contentType, method];
  } else {
    delete data.icon;
    return [data];
  }
};

// DataProvider factory
export const dataProviderFactory: DataProviderFactory = (apiURL, customSettings = {}, tokenName = 'token') => {
  const settings = { ...defaultSettings, ...customSettings };
  const options = { headers: settings.headers };
  const client = createClient(apiURL, settings);

  setInterceptors(client, tokenName);

  return {
    getFilterList: async (resource: string) => {
      const url = `${apiURL}/${resource}/filters`;
      const res = await client({ url, ...options });

      return {
        data: res.data.data,
        total: res.data.total || res.data.count || 0,
      };
    },
    getList: async (resource, params) => {
      const query = getQueryFromParams({ ...params, ...customSettings });
      const url = `${apiURL}/${resource}?${stringify(query)}`;
      const res = await client({ url, ...options });

      return {
        data: res.data.data,
        total: res.data.total || 0,
      };
    },
    getOne: async (resource, params) => {
      const url = `${apiURL}/${resource}/${params.id}`;
      const res = await client.get(url);

      return { data: res.data };
    },
    getMany: async (resource, params) => {
      const query = getIds(params);
      const url = `${apiURL}/${resource}?${query}`;
      const res = await client({ url, ...options });

      return {
        data: res.data.data,
        total: res.data.total,
      };
    },
    getManyReference: async (resource, params) => {
      const query = getQueryFromParams({ ...params, ...customSettings });
      const url = `${apiURL}/${resource}?${stringify(query)}`;
      const res = await client({ url, ...options });

      return {
        data: res.data.data,
        total: res.data.total,
      };
    },
    create: async (resource, params) => {
      const url = `${apiURL}/${resource}`;
      let settings = {};
      let data = undefined;
      let contentType = undefined;

      for (let property in params.data) {
        if (params.data[property] === null || params.data[property] === undefined) {
          delete params.data[property];
        }
      }
      // channel_releases: the create endpoint reads the scheduled date as `release_at`
      // (edit + form/display use `released_at`). Remap so a scheduled "Add channel
      // release" persists the date. See TVR-1354.
      if (resource === 'channel_releases' && params.data.released_at != null) {
        params.data.release_at = params.data.released_at;
        delete params.data.released_at;
      }
      if (['apps', 'collections'].indexOf(resource) > -1) {
        [data, contentType] = parseIntoFormData(params.data);
      }
      if (!data) {
        data = { ...params.data };
      }

      if (contentType) {
        settings = {
          ...client.prototype.headers,
          headers: {
            'Content-Type': contentType,
          },
        };
      }

      try {
        const res = await client.post(url, data, settings);

        return {
          data: {
            id: res.data.id,
            ...res.data.attributes,
          },
        };
      } catch (error: any) {
        validationErrorHandling(error);
        throw error;
      }
    },
    update: async (resource, params) => {
      const url = `${apiURL}/${resource}/${params.id}`;
      let data = {
        ...params.data,
      };
      delete data.id;

      let settings = {};
      let method = undefined;
      let contentType = undefined;

      if (['apps', 'collections'].indexOf(resource) > -1) {
        [data, contentType, method] = parseIntoFormData(data, 'put');
      }
      if (contentType) {
        settings = {
          ...client.prototype.headers,
          headers: {
            'Content-Type': contentType,
          },
        };
      }
      if (!method) method = 'put';

      try {
        const res = method === 'put' ? await client.put(url, data, settings) : await client.post(url, data, settings);

        return {
          data: res.data,
        };
      } catch (error: any) {
        validationErrorHandling(error);
        throw error;
      }
    },
    updateMany: (_resource, _params) => {
      return Promise.reject<UpdateManyResult>();
    },
    delete: async (resource, params) => {
      const url = `${apiURL}/${resource}/${params.id}`;
      const res = await client.delete(url);

      return { data: res.data };
    },
    deleteMany: async (resource, params) => {
      const query = getIds(params, settings.arrayFormat);
      const url = `${apiURL}/${resource}/${query}`;
      const res = await client.delete(url);

      return { data: res.data };
    },
    get: async (endpoint: string) => {
      const url = `${apiURL}/${endpoint}`;
      const res = await client.get(url);

      return res.data;
    },
    post: async (endpoint: string, data: any) => {
      const url = `${apiURL}/${endpoint}`;
      const config = data instanceof FormData ? { headers: { 'Content-Type': 'multipart/form-data' } } : {};

      try {
        const res = await client.post(url, data, config);
        return res.data;
      } catch (error: any) {
        // 422 only; everything else rethrows untouched. See `postValidationErrorHandling` (TVR-2148).
        postValidationErrorHandling(error);
        throw error;
      }
    },
    download: async (endpoint: string) => {
      const url = `${apiURL}/${endpoint}`;
      const res = await client.get(url, { responseType: 'blob' });

      if (res) {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        const contentDispositionHeader = res.headers['content-disposition'] as string;

        link.href = url;

        let filenames = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDispositionHeader);

        if (filenames && filenames.length > 0) {
          let filename = filenames[1];

          link.setAttribute('download', filename); //or any other extension
          document.body.appendChild(link);
          link.click();
        }
      }
      return res.data;
    },
    // Download a file using a full absolute URL (e.g. download_url returned directly by the API).
    // Triggers a browser file-save and returns the Blob for in-page use (e.g. image preview).
    downloadByPath: async (absoluteUrl: string, filename: string): Promise<Blob> => {
      const res = await client.get(absoluteUrl, { responseType: 'blob' });
      const blob: Blob = res.data;

      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);

      return blob;
    },
    // Fetch a blob from a full absolute URL without triggering a download — used for in-page previews.
    fetchBlob: async (absoluteUrl: string): Promise<Blob> => {
      const res = await client.get(absoluteUrl, { responseType: 'blob' });
      return res.data;
    },
    getUserProfile: async () => {
      const storedProfile = sessionStorage.getItem('profile');

      if (storedProfile) {
        return Promise.resolve({
          data: JSON.parse(storedProfile),
        });
      }

      // No stored profile yet, get one for auth user
      const url = `${apiURL}/account`;
      const res = await client.get(url);

      if (res) {
        sessionStorage.setItem('profile', JSON.stringify(res.data));
      }

      return { data: res.data };
    },
    updateUserProfile: async (resource: AccountPayload, params: { onSuccess: OnSuccess; onFailure: OnError }) => {
      const url = `${apiURL}/account`;
      const res = await client.patch(url, resource);

      if (res) {
        sessionStorage.setItem('profile', JSON.stringify({ ...res.data }));
      }

      return { data: res.data };
    },
    getDiagnostics: async (resource: string, { serial, type }: { serial: string; type?: string }) => {
      const url = `${apiURL}/${resource}/${serial}/reports` + (type ? `/${type}` : '');
      const res = await client({ url, ...options });

      return {
        data: res.data.data,
        total: res.data.total ?? 0,
      };
    },
    getCurrentlyOnlineCount: async () => {
      const url = `${apiURL}/units/currently_online_count`;
      const res = await client({ url, ...options });

      return {
        data: res.data.data,
      };
    },
    // Command System Methods
    getUnitCommands: async (unitId: number): Promise<{ data: Command[] }> => {
      try {
        const url = `${apiURL}/units/${unitId}/commands`;
        const res = await client.get(url);
        return { data: res.data.data };
      } catch (error) {
        handleCommandError(error);
      }
    },
    getUnitCommand: async (unitId: number, commandId: number): Promise<{ data: Command }> => {
      try {
        const url = `${apiURL}/units/${unitId}/commands/${commandId}`;
        const res = await client.get(url);
        return { data: res.data.data };
      } catch (error) {
        handleCommandError(error);
      }
    },
    getCommandParameters: async (commandId: number): Promise<{ data: CommandParameter[] }> => {
      try {
        const url = `${apiURL}/commands/${commandId}/parameters`;
        const res = await client.get(url);
        return { data: res.data.data };
      } catch (error) {
        handleCommandError(error);
      }
    },
    executeCommand: async (
      unitId: number,
      commandId: number,
      parameters: Record<string, any>,
    ): Promise<{ data: CommandExecution }> => {
      try {
        const url = `${apiURL}/units/${unitId}/commands/${commandId}/execute`;
        const res = await client.post(url, { parameters });
        return { data: res.data.data };
      } catch (error) {
        handleCommandError(error);
      }
    },
    getCommandExecutions: async (
      unitId: number,
      filters?: {
        status?: ExecutionStatus;
        command_id?: number;
        page?: number;
        per_page?: number;
      },
    ): Promise<{ data: CommandExecution[]; total: number }> => {
      try {
        const queryParams = new URLSearchParams();
        if (filters?.status) queryParams.append('status', filters.status);
        if (filters?.command_id) queryParams.append('command_id', filters.command_id.toString());
        if (filters?.page) queryParams.append('page', filters.page.toString());
        if (filters?.per_page) queryParams.append('per_page', filters.per_page.toString());

        const url = `${apiURL}/units/${unitId}/command-executions${
          queryParams.toString() ? '?' + queryParams.toString() : ''
        }`;
        const res = await client.get(url);

        return {
          data: res.data.data,
          total: res.data.total || res.data.data.length,
        };
      } catch (error) {
        handleCommandError(error);
      }
    },
    getCommandExecution: async (unitId: number, executionId: number): Promise<{ data: CommandExecution }> => {
      try {
        const url = `${apiURL}/units/${unitId}/command-executions/${executionId}`;
        const res = await client.get(url);
        return { data: res.data.data };
      } catch (error) {
        handleCommandError(error);
      }
    },
    getCommandExecutionAckMessage: async (
      unitId: number,
      executionId: number,
    ): Promise<{
      data: {
        execution_id: number;
        feedback_output: string;
        feedback_completed: boolean;
        feedback_completed_at: string | null;
        size_bytes: number;
      };
    }> => {
      try {
        const url = `${apiURL}/units/${unitId}/command-executions/${executionId}/ack-message`;
        const res = await client.get(url);
        return { data: res.data };
      } catch (error: any) {
        // If ACK message not found, return empty response
        if (error.status === 404 || error.response?.status === 404) {
          return {
            data: {
              execution_id: executionId,
              feedback_output: '',
              feedback_completed: false,
              feedback_completed_at: null,
              size_bytes: 0,
            },
          };
        }
        handleCommandError(error);
      }
    },
  };
};

// Export wrapped dataProvider
export const dataProvider = addRefreshAuthToDataProvider(
  dataProviderFactory(process.env.REACT_APP_API_URL || ''),
  checkAuth,
);

/**
 * react-admin's transport format for a validation message that carries interpolation arguments.
 *
 * Duplicated, knowingly: `src/components/ServerValidationErrors/index.tsx` holds the same literal for
 * the reading end. Sharing it would make this provider import from a component module — the wrong way
 * round — and react-admin does not export its own copy (`ValidationErrorSpecialFormatPrefix` in
 * `ra-core/src/form/validation/ValidationError.tsx`), so neither side can import it from the library.
 * Change one, change the other.
 */
const VALIDATION_ERROR_PREFIX = '@@react-admin@@';

/**
 * A message prefix that is a **translation key** rather than the opening clause of a sentence:
 * dot-separated segments of key-safe characters, at least one dot, no whitespace, no empty segment.
 *
 * Matches `some.key`, `validation.in`, `resources.commands.errors.name_taken`.
 * Does not match `The data type must be one of` (whitespace), `Note` (no dot), `N.B.` (trailing
 * dot ⇒ empty final segment), `http` (from a URL) — i.e. every way a real sentence reaches a colon.
 */
const TRANSLATION_KEY = /^[A-Za-z0-9_-]+(?:\.[A-Za-z0-9_-]+)+$/;

/**
 * Turns one Laravel message into something react-admin can render on an input.
 *
 * The API sends either a finished English sentence or a translation key, the latter optionally
 * suffixed with `":arg1,arg2"`. The key is what gets translated; the args are its interpolation
 * values. react-hook-form can only hold a **string** per field, so an error that has args travels
 * in react-admin's own escape hatch for exactly this (`ra-core/src/form/useInput.ts`), which
 * `<ValidationError>` parses back before translating. Anything without args stays a plain string —
 * it needs no encoding, and a raw `@@react-admin@@…` would be what the user reads anywhere the
 * message is shown as text.
 *
 * **Why the split is conditional (TVR-1915).** A colon is not a reliable separator: it is ordinary
 * punctuation in English. Splitting unconditionally cut four real toverzicht-api messages in half —
 * `'The data type must be one of: string, integer, boolean, float, array, object.'` and its
 * `ui_component` twin, in Create/UpdateCommandParameterRequest::messages() — so the user was told
 * the value must be one of, and then not told what, because the list had been demoted to positional
 * args no catalogue entry interpolates. The discriminator is therefore the shape of the part
 * **before the first colon**: it splits only when that part looks like a translation key
 * (`TRANSLATION_KEY` above). The test is applied to the prefix alone, never to the whole string,
 * because args are arbitrary values and may legitimately contain spaces, commas and punctuation.
 *
 * **The args branch is capability, not dead code someone forgot.** As of TVR-1915 no live endpoint
 * emits the key form: toverzicht-api has no `validation.php` override and every `messages()`
 * override returns a finished sentence, so in production every message takes the pass-through
 * branch. AC5 requires the args path to work regardless — it is the only route by which validation
 * messages could ever become translatable, and removing it would close that door. It is covered by
 * a deliberately synthetic test (`dataProvider.validationErrorArgs.test.tsx`).
 */
function toValidationMessage(raw: string): string {
  const separator = raw.indexOf(':');

  // No colon, or a colon inside a sentence: the message is already what the user should read.
  if (separator === -1 || !TRANSLATION_KEY.test(raw.slice(0, separator))) {
    return raw;
  }

  const key = raw.slice(0, separator);
  // Split at the FIRST colon only, so a colon inside an arg value survives into that value.
  const args = raw
    .slice(separator + 1)
    .split(',')
    .filter((arg) => arg.length > 0);

  if (args.length === 0) {
    return key;
  }

  // NOTE: `args` is a list, and `<ValidationError>` spreads it into the translation arguments, so
  // the placeholders in the catalogue entry are positional — `%{0}`, `%{1}` — not named.
  return `${VALIDATION_ERROR_PREFIX}${JSON.stringify({ message: key, args })}`;
}

/**
 * Laravel's per-field 422 arrays, flattened into the one shape this app emits everywhere.
 *
 * Laravel's 422 body is `{ errors: { field: ["message", …] } }`. It is translated **here**, once,
 * into the flat `{ field: string }` shape react-admin's own `setSubmissionErrors` understands, so
 * every form in the app gets its field errors on its inputs without any per-form code. Emitting the
 * previous `{ field: { message, args } }` object instead made `setSubmissionErrors` recurse and call
 * `setError('field.message')` — a path no input is bound to — so a 422 reached the user as nothing
 * at all (TVR-1915).
 *
 * Only the **first** message per field is kept, which is the behaviour this has always had.
 *
 * Extracted from `validationErrorHandling` under TVR-2144 so the command methods
 * (`handleCommandError`) and the raw `post` passthrough emit the *same* shape rather than three
 * dialects of it. A body that is absent, `null`, or not an object yields `{}` rather than throwing,
 * and a field whose value is not a usable string is skipped rather than guessed at.
 */
function mapLaravelErrors(errors: unknown): Record<string, string> {
  const errorMessages: Record<string, string> = {};

  if (errors == null || typeof errors !== 'object') {
    return errorMessages;
  }

  for (const [field, value] of Object.entries(errors as Record<string, unknown>)) {
    // Laravel sends a list per field; a bare string is tolerated rather than trusted.
    const raw = Array.isArray(value) ? value[0] : value;
    if (typeof raw !== 'string' || raw.length === 0) {
      continue;
    }
    errorMessages[field] = toValidationMessage(raw);
  }

  return errorMessages;
}

/** The `HttpError` a Laravel 422 response body becomes, shared by every entry point below. */
function buildValidationError(data: any): HttpError {
  return new HttpError('validation.error', 422, {
    errors: mapLaravelErrors(data?.errors),
    // Kept so callers can act on the conflicting record instead of only showing
    // the message. `POST /v3/api/commands` returns the restorable command here
    // when the name belongs to a soft-deleted one (TVR-1580).
    ...(data?.trashed_command ? { trashed_command: data.trashed_command } : {}),
  });
}

/**
 * Handles validation errors returned from the server, for `create` and `update`.
 */
function validationErrorHandling(error: any) {
  if (error.response && error.response.status === 422) {
    throw buildValidationError(error.response.data);
  }
}

/**
 * The same translation, for the raw `post` passthrough (TVR-2148).
 *
 * `post` used to reject with the untouched axios error, so `err.status` and `err.body` were both
 * `undefined` and an `HttpError`-shaped guard could never match — which is why the 422 branches in
 * `BatchAssignModal` and `BatchAssignToChannelModal` were dead in production and the user always
 * got the generic fallback instead of the API's message.
 *
 * **Only 422 is converted.** Every other status, and every network failure, still rejects with the
 * untouched axios error, because existing call sites recognise a failure by its axios shape. The
 * two on this path are both `switch (error.response.status)` blocks behind an
 * `isAxiosError(error) && error.response` guard:
 *
 * - `src/resources/units/UnitShow.tsx:49`
 * - `src/resources/dashboard/MissingUnits.tsx:35`
 *
 * The same duck-typing exists on `download`, which this function does **not** wrap — `download`
 * still rejects with the raw axios error at every status. Noted here because any future attempt to
 * give `download` the same treatment has to keep these two working, and they are not alike:
 *
 * - `src/resources/units/components/useReportDownloadHandler.ts:38` — the same guard plus a
 *   `switch (error.response.status)`
 * - `src/resources/units/components/useUnitsCsvExport.ts:40` — a bare `isAxiosError(error)`, no
 *   status branch at all
 *
 * For the same reason the axios identity is carried over onto the `HttpError`: `isAxiosError` is
 * duck-typed on `payload.isAxiosError === true` (`axios/lib/helpers/isAxiosError.js`), so keeping
 * the flag and the original `response` means even a 422 reaching one of those `switch (status)`
 * blocks still lands in the branch it lands in today, while `status` and `body` become readable.
 */
function postValidationErrorHandling(error: any) {
  if (error?.response?.status !== 422) {
    return;
  }

  throw Object.assign(buildValidationError(error.response.data), {
    isAxiosError: true,
    response: error.response,
  });
}

/**
 * Handles command-specific errors returned from the server.
 */
function handleCommandError(error: any): never {
  const status = error.status || error.response?.status;

  switch (status) {
    case 409:
      // Unit offline or command inactive
      throw new HttpError(error.body?.message || 'Unit is offline or command is not available', 409);

    case 422: {
      // Validation errors - transform to React Admin format.
      //
      // Mapped through `mapLaravelErrors` (TVR-2144) so this door emits the same flat
      // `{ field: string }` body as `create`/`update`. Rethrowing Laravel's raw arrays here was
      // harmless only while no react-admin form was built on a command method: `setSubmissionErrors`
      // recurses into an object value, so `{ field: ["msg"] }` became `setError('field.0')` — a path
      // no input is bound to — which is exactly the defect TVR-1915 fixed for the CRUD path.
      //
      // Both sources are still read: a command method can arrive here as an already-built
      // `HttpError` (`error.body`) or as a raw axios error (`error.response.data`). The `message`
      // argument is deliberately unchanged — `CommandExecutionForm` builds its notification from it.
      const validationErrors = error.body?.errors || error.response?.data?.errors;
      throw new HttpError(error.body?.message || error.response?.data?.message || 'Validation failed', 422, {
        errors: mapLaravelErrors(validationErrors),
      });
    }

    case 429:
      // Rate limiting
      throw new HttpError('Too many requests. Please wait a moment and try again.', 429);

    default:
      // Re-throw original error
      throw error;
  }
}
